import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getNotificationCount, getNotifications } from "../services/notificationService";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [leadWaitingCount, setLeadWaitingCount] = useState(0);
  const [clientWaitingCount, setClientWaitingCount] = useState(0);

  // Fetch notification count (no polling)
  const fetchCount = useCallback(async () => {
    try {
      const data = await getNotificationCount();
      setCount(data.count);
      return data.count;
    } catch (e) {
      setError("Failed to fetch notification count.");
      return null;
    }
  }, []);

  // Fetch notifications (lazy load)
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getNotifications();
      setNotifications(data.notifications || []);
      setLeadWaitingCount(data.leadWaitingCount ?? 0);
      setClientWaitingCount(data.clientWaitingCount ?? 0);
      setLoaded(true);
      return data;
    } catch (e) {
      setError("Failed to fetch notifications.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh both count and notifications immediately
  // Used after actions like scheduling a call
  const refreshNotifications = useCallback(async () => {
    try {
      // Fetch both count and notifications in parallel
      const [countResult, notificationsResult] = await Promise.all([
        getNotificationCount(),
        getNotifications(),
      ]);

      setCount(countResult.count || 0);
      setNotifications(notificationsResult.notifications || []);
      setLeadWaitingCount(notificationsResult.leadWaitingCount ?? 0);
      setClientWaitingCount(notificationsResult.clientWaitingCount ?? 0);
      setLoaded(true);
      setError(null);

      return {
        count: countResult.count || 0,
        notifications: notificationsResult.notifications || [],
        leadWaitingCount: notificationsResult.leadWaitingCount ?? 0,
        clientWaitingCount: notificationsResult.clientWaitingCount ?? 0,
      };
    } catch (e) {
      setError("Failed to refresh notifications.");
      return null;
    }
  }, []);

  // Route change detection for notification count
  const location = useLocation();
  useEffect(() => {
    // Skip fetching notification counts on auth-related public pages
    try {
      const pathname = location?.pathname || "";
      // Matches /auth/*, /reset-password and /forgot-password (top-level links from emails)
      if (/^\/(auth|reset-password|forgot-password)(\/|$)/i.test(pathname)) return;
    } catch (e) {
      // ignore and proceed
    }

    fetchCount();
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <NotificationContext.Provider
      value={{
        count,
        notifications,
        loading,
        loaded,
        error,
        leadWaitingCount,
        clientWaitingCount,
        fetchCount,
        fetchNotifications,
        refreshNotifications,
        setLoaded,
        setNotifications,
        setCount,
        setError,
        setLoading,
        setLeadWaitingCount,
        setClientWaitingCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
